import { useEffect, useMemo, useState } from 'react'
import { X, Save } from 'lucide-react'
import { CATEGORY_ICON_OPTIONS } from '../../../config/category-icons'
import { useCategories, useCreateCategory, useUpdateCategory, type ApiCategory } from '@/modules/categories-api'
import { CategoryIconPreview } from '../../components/CategoryIconPreview'
import { useToast } from '../../components/Toast'

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

interface Props {
  open: boolean
  editing: ApiCategory | null
  onClose: () => void
}

export function CategoryFormDialog({ open, editing, onClose }: Props) {
  const { data: categories } = useCategories()
  const createMut = useCreateCategory()
  const updateMut = useUpdateCategory()
  const push = useToast((s) => s.push)

  const [label, setLabel] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [icon, setIcon] = useState('package')
  const [description, setDescription] = useState('')
  const [order, setOrder] = useState(100)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    if (editing) {
      setLabel(editing.label); setSlug(editing.slug); setIcon(editing.icon)
      setDescription(editing.description ?? ''); setOrder(editing.order)
      setSlugTouched(true)
    } else {
      setLabel(''); setSlug(''); setIcon('package'); setDescription(''); setOrder((Math.max(0, ...categories.map((c) => c.order)) || 0) + 10); setSlugTouched(false)
    }
    setErrors({})
  }, [open, editing, categories])

  useEffect(() => {
    if (!editing && !slugTouched && label) setSlug(slugify(label))
  }, [label, editing, slugTouched])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const slugDupe = useMemo(() => {
    if (!slug) return false
    return categories.some((c) => c.slug === slug && c.id !== editing?.id)
  }, [slug, categories, editing])

  if (!open) return null

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const busy = createMut.isPending || updateMut.isPending

  const handleSave = async () => {
    const errs: Record<string, string> = {}
    if (!label.trim()) errs.label = 'Vui lòng nhập tên danh mục'
    if (!slug.trim())  errs.slug  = 'Slug không được rỗng'
    if (slugDupe)      errs.slug  = 'Slug đã tồn tại'
    if (Object.keys(errs).length) { setErrors(errs); return }

    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, input: { label: label.trim(), slug, icon, description, order } })
        push('success', `Đã cập nhật "${label}"`)
      } else {
        await createMut.mutateAsync({ label: label.trim(), slug, icon, description, order })
        push('success', `Đã thêm "${label}"`)
      }
      onClose()
    } catch (e: any) {
      push('error', e?.message ?? 'Lỗi mạng')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="h-14 px-5 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-text">{editing ? 'Sửa danh mục' : 'Thêm danh mục'}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Tên danh mục <span className="text-danger">*</span></div>
            <input value={label} onChange={(e) => { setLabel(e.target.value); setErrors((er) => ({ ...er, label: '' })) }} className={inputCls(errors.label)} placeholder="Xe máy điện" />
            {errors.label && <div className="text-xs text-danger mt-1">{errors.label}</div>}
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Slug URL <span className="text-danger">*</span></div>
            <input
              value={slug}
              onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); setErrors((er) => ({ ...er, slug: '' })) }}
              className={inputCls(errors.slug)}
              placeholder="xe-may-dien"
            />
            <div className="text-xs text-muted mt-1">URL: <span className="font-mono">/danh-muc/{slug || '...'}</span></div>
            {errors.slug && <div className="text-xs text-danger mt-1">{errors.slug}</div>}
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Biểu tượng</div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                <CategoryIconPreview name={icon} className="w-5 h-5" />
              </div>
              <select value={icon} onChange={(e) => setIcon(e.target.value)} className={inputCls()}>
                {CATEGORY_ICON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Thứ tự hiển thị</div>
              <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} className={inputCls()} />
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Mô tả (tùy chọn)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
              placeholder="Mô tả ngắn..."
            />
          </label>
        </div>

        <div className="px-5 py-4 bg-slate-50 flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="h-10 px-4 rounded-md border border-border text-text hover:bg-surface text-sm font-medium disabled:opacity-50">
            Hủy
          </button>
          <button onClick={handleSave} disabled={busy} className="h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {busy ? 'Đang lưu...' : editing ? 'Lưu thay đổi' : 'Tạo danh mục'}
          </button>
        </div>
      </div>
    </div>
  )
}
